
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

export default function Footer() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  useEffect(() => {
    // Load Calendly widget script
    const script = document.createElement('script');
    script.src = 'https://assets.calendly.com/assets/external/widget.js';
    script.async = true;
    document.body.appendChild(script);

    return () => {
      // Cleanup script on unmount
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d58nq36isj01hmefquo0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', phone: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  return (
    <footer className="bg-olive-900 text-white">
      {/* Calendly Booking Section */}
      <div id="termin-buchen" className="py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <span className="text-gold-500 text-sm font-medium tracking-[0.2em] uppercase mb-4 block">
              Vereinbare einen Termin
            </span>
            <h2 className="font-serif text-4xl md:text-5xl font-semibold mb-4 text-olive-900">
              Termin buchen
            </h2>
            <div className="w-16 h-px bg-gold-300 mx-auto mb-6"></div>
            <p className="text-lg text-olive-700 max-w-2xl mx-auto font-light">
              Wähle einen passenden Termin für deine Behandlung. Ich freue mich auf dich!
            </p>
          </div>
          
          {/* Calendly Widget */}
          <div 
            className="calendly-inline-widget rounded-3xl overflow-hidden shadow-xl border border-olive-100" 
            data-url="https://calendly.com/theresa-weiss1991/30min?locale=de"
            style={{ minWidth: '320px', height: '700px' }}
          ></div>

          {/* Important Information Section */}
          <div className="mt-20 bg-gradient-to-br from-olive-50 to-gold-50 rounded-3xl p-10 md:p-14 border border-olive-100">
            <div className="max-w-4xl mx-auto">
              <h3 className="font-serif text-3xl font-semibold text-olive-900 mb-4 text-center">
                Wichtige Informationen für deinen Besuch
              </h3>
              <div className="w-12 h-px bg-gold-300 mx-auto mb-8"></div>
              <p className="text-olive-700 mb-10 text-center font-light">
                Damit der Praxisablauf reibungslos funktioniert, bitte ich dich um Beachtung folgender Punkte:
              </p>

              <div className="grid md:grid-cols-2 gap-6">
                {/* Handtuch */}
                <div className="bg-white rounded-2xl p-7 shadow-sm hover:shadow-lg transition-all duration-300 border border-olive-100">
                  <div className="flex items-start gap-5">
                    <div className="w-14 h-14 bg-gradient-to-br from-gold-200 to-gold-300 rounded-xl flex items-center justify-center flex-shrink-0 shadow-md">
                      <i className="ri-shirt-line text-2xl text-olive-900"></i>
                    </div>
                    <div>
                      <h4 className="font-semibold text-olive-900 mb-2">Handtuch</h4>
                      <p className="text-olive-600 text-sm leading-relaxed font-light">
                        Bitte bringe zu jeder Behandlung ein eigenes großes Handtuch (z. B. ein Saunatuch) mit.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Terminabsagen */}
                <div className="bg-white rounded-2xl p-7 shadow-sm hover:shadow-lg transition-all duration-300 border border-olive-100">
                  <div className="flex items-start gap-5">
                    <div className="w-14 h-14 bg-gradient-to-br from-gold-200 to-gold-300 rounded-xl flex items-center justify-center flex-shrink-0 shadow-md">
                      <i className="ri-calendar-close-line text-2xl text-olive-900"></i>
                    </div>
                    <div>
                      <h4 className="font-semibold text-olive-900 mb-2">Terminabsagen</h4>
                      <p className="text-olive-600 text-sm leading-relaxed font-light">
                        Solltest du einen Termin nicht wahrnehmen können, sage diesen bitte mindestens 24 Stunden vorher ab. Bitte habe Verständnis dafür, dass nicht rechtzeitig abgesagte Termine in voller Höhe in Rechnung gestellt werden müssen.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Gesundheit geht vor */}
                <div className="bg-white rounded-2xl p-7 shadow-sm hover:shadow-lg transition-all duration-300 border border-olive-100">
                  <div className="flex items-start gap-5">
                    <div className="w-14 h-14 bg-gradient-to-br from-gold-200 to-gold-300 rounded-xl flex items-center justify-center flex-shrink-0 shadow-md">
                      <i className="ri-heart-pulse-line text-2xl text-olive-900"></i>
                    </div>
                    <div>
                      <h4 className="font-semibold text-olive-900 mb-2">Gesundheit geht vor</h4>
                      <p className="text-olive-600 text-sm leading-relaxed font-light">
                        Bei akuten Krankheitssymptomen (wie Fieber, starken Erkältungssymptomen oder Magen-Darm-Beschwerden) bitte ich dich, den Termin abzusagen. Eine physiotherapeutische Behandlung kann den Körper bei einem Infekt zusätzlich belasten und die Symptome verstärken.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Anfahrt & Parken */}
                <div className="bg-white rounded-2xl p-7 shadow-sm hover:shadow-lg transition-all duration-300 border border-olive-100">
                  <div className="flex items-start gap-5">
                    <div className="w-14 h-14 bg-gradient-to-br from-gold-200 to-gold-300 rounded-xl flex items-center justify-center flex-shrink-0 shadow-md">
                      <i className="ri-parking-box-line text-2xl text-olive-900"></i>
                    </div>
                    <div>
                      <h4 className="font-semibold text-olive-900 mb-2">Anfahrt &amp; Parken</h4>
                      <p className="text-olive-600 text-sm leading-relaxed font-light">
                        Direkt vor Ort stehen dir zwei Parkplätze rechts vom Carport zur Verfügung. Bitte nutze ausschließlich diese Stellflächen.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Contact Section */}
      <div id="kontakt" className="py-28">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-20">
            <span className="text-gold-300 text-sm font-medium tracking-[0.2em] uppercase mb-4 block">
              Kontakt
            </span>
            <h2 className="font-serif text-4xl md:text-5xl font-semibold mb-4">
              Bist du bereit?
            </h2>
            <div className="w-16 h-px bg-gold-300 mx-auto mb-6"></div>
            <p className="text-lg text-white/80 max-w-2xl mx-auto font-light">
              Lass uns gemeinsam deinen Weg zur Genesung starten. Schreib mir eine Nachricht oder buche direkt einen Termin.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12 max-w-6xl mx-auto">
            {/* Contact Form */}
            <div className="bg-white/5 backdrop-blur-sm p-10 rounded-3xl border border-white/10">
              <h3 className="font-serif text-2xl font-semibold mb-8">Kontaktformular</h3>
              <form onSubmit={handleSubmit} data-readdy-form id="contact-form">
                <div className="space-y-5">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium mb-2 text-white/90">
                      Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-5 py-3.5 rounded-xl bg-white/5 border border-white/20 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-gold-300/50 focus:border-gold-300/50 text-sm transition-all"
                      placeholder="Dein Name"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium mb-2 text-white/90">
                      E-Mail *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-5 py-3.5 rounded-xl bg-white/5 border border-white/20 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-gold-300/50 focus:border-gold-300/50 text-sm transition-all"
                      placeholder="deine.email@beispiel.de"
                    />
                  </div>
                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium mb-2 text-white/90">
                      Telefon
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full px-5 py-3.5 rounded-xl bg-white/5 border border-white/20 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-gold-300/50 focus:border-gold-300/50 text-sm transition-all"
                      placeholder="+43 123 456789"
                    />
                  </div>
                  <div>
                    <label htmlFor="message" className="block text-sm font-medium mb-2 text-white/90">
                      Nachricht *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      maxLength={500}
                      rows={4}
                      className="w-full px-5 py-3.5 rounded-xl bg-white/5 border border-white/20 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-gold-300/50 focus:border-gold-300/50 resize-none text-sm transition-all"
                      placeholder="Wie kann ich dir helfen?"
                    ></textarea>
                    <p className="text-xs text-white/50 mt-2">
                      Maximal 500 Zeichen
                    </p>
                  </div>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-gold-300 text-olive-900 px-6 py-4 rounded-xl font-semibold hover:bg-gold-400 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap hover:shadow-lg"
                  >
                    {isSubmitting ? 'Wird gesendet...' : 'Nachricht senden'}
                  </button>
                  {submitStatus === 'success' && (
                    <p className="text-sm text-white bg-green-500/20 p-4 rounded-xl border border-green-500/30">
                      Vielen Dank! Deine Nachricht wurde erfolgreich gesendet.
                    </p>
                  )}
                  {submitStatus === 'error' && (
                    <p className="text-sm text-white bg-red-500/20 p-4 rounded-xl border border-red-500/30">
                      Es gab einen Fehler. Bitte versuche es erneut.
                    </p>
                  )}
                </div>
              </form>
            </div>

            {/* Contact Info */}
            <div className="space-y-10">
              <div>
                <h3 className="font-serif text-2xl font-semibold mb-8">Kontaktinformationen</h3>
                <div className="space-y-5">
                  <div className="flex items-start gap-5 group">
                    <div className="w-14 h-14 bg-white/5 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-gold-300/20 transition-colors">
                      <i className="ri-map-pin-line text-2xl text-gold-300"></i>
                    </div>
                    <div>
                      <p className="font-semibold mb-1">Adresse</p>
                      <p className="text-white/70 text-sm font-light">
                        Achenlohe 13<br />
                        5222 Munderfing, Österreich
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-5 group">
                    <div className="w-14 h-14 bg-white/5 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-gold-300/20 transition-colors">
                      <i className="ri-phone-line text-2xl text-gold-300"></i>
                    </div>
                    <div>
                      <p className="font-semibold mb-1">Telefon</p>
                      <p className="text-white/70 text-sm font-light">+43 660 3450403</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-5 group">
                    <div className="w-14 h-14 bg-white/5 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-gold-300/20 transition-colors">
                      <i className="ri-mail-line text-2xl text-gold-300"></i>
                    </div>
                    <div>
                      <p className="font-semibold mb-1">E-Mail</p>
                      <p className="text-white/70 text-sm font-light">Theresa.weiss1991@gmail.com</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-5 group">
                    <div className="w-14 h-14 bg-white/5 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-gold-300/20 transition-colors">
                      <i className="ri-time-line text-2xl text-gold-300"></i>
                    </div>
                    <div>
                      <p className="font-semibold mb-1">Öffnungszeiten</p>
                      <p className="text-white/70 text-sm font-light">
                        Dienstag: 08:00 - 20:00 Uhr
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="pt-8 border-t border-white/10">
                <p className="font-semibold mb-5">Folge mir</p>
                <div className="flex gap-4">
                  <a 
                    href="https://www.instagram.com" 
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-14 h-14 bg-white/5 rounded-xl flex items-center justify-center hover:bg-gold-300/20 transition-all duration-300 cursor-pointer"
                    aria-label="Instagram"
                  >
                    <i className="ri-instagram-line text-2xl text-gold-300"></i>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Footer */}
      <div className="border-t border-white/10 py-8">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-white/60 font-light">
              © 2025 Physiotherapie Theresa Mühlbacher. Alle Rechte vorbehalten.
            </p>
            <div className="flex flex-wrap gap-6 text-sm">
              <Link to="/impressum" className="text-white/60 hover:text-gold-300 transition-colors cursor-pointer whitespace-nowrap">
                Impressum
              </Link>
              <Link to="/datenschutz" className="text-white/60 hover:text-gold-300 transition-colors cursor-pointer whitespace-nowrap">
                Datenschutzerklärung
              </Link>
              <a 
                href="https://readdy.ai/?ref=logo" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="text-white/60 hover:text-gold-300 transition-colors cursor-pointer whitespace-nowrap"
              >
                Powered by Readdy
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
